import Breadcrumb from '../components/layout/Breadcrumb';
import OnThisPage, { ON_THIS_PAGE_LAYOUT } from '../components/layout/OnThisPage';
import Card from '../components/ui/Card';
import { usePageTitle } from '../hooks/usePageTitle';
import { useScrollSpy } from '../hooks/useScrollSpy';
import {
  CONTACT_FACTOR,
  CONTACT_MINISTRY,
  CONTACT_ONTRAC,
  CONTACT_REGIONS,
  CONTACT_SECTIONS,
  CONTACT_TTISS,
  CONTACT_WEBSITE_EMAIL,
  ContactPerson,
  contactSectionIds,
} from '../content/contact';

const sectionIds = contactSectionIds();

function PersonCard({ person }: { person: ContactPerson }) {
  return (
    <Card>
      <h4 className="text-lg font-semibold text-gray-900">{person.name}</h4>
      {person.role ? <p className="text-gray-700 mt-1">{person.role}</p> : null}
      {person.detail ? <p className="text-gray-600 italic mt-1">{person.detail}</p> : null}
      {person.organization ? <p className="text-gray-700 mt-1">{person.organization}</p> : null}
      {person.email ? (
        <a
          href={`mailto:${person.email}`}
          className="block mt-3 text-primary hover:text-primary-dark break-all"
        >
          {person.email}
        </a>
      ) : null}
      {person.phone ? <p className="text-gray-700 mt-1">{person.phone}</p> : null}
      {person.website ? (
        <a
          href={person.website.href}
          target="_blank"
          rel="noopener noreferrer"
          className="block mt-3 text-primary hover:text-primary-dark"
        >
          {person.website.label}
        </a>
      ) : null}
    </Card>
  );
}

export default function Contact() {
  usePageTitle('Contact Information');
  const activeSectionId = useScrollSpy(sectionIds);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Breadcrumb items={[{ label: 'Home', to: '/' }, { label: 'Contact' }]} />
      <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">Contact Information</h1>

      <div className={ON_THIS_PAGE_LAYOUT}>
        <OnThisPage items={CONTACT_SECTIONS} activeId={activeSectionId} />
        <div className="min-w-0 space-y-12">
          <section id="orbcon" className="scroll-mt-24">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">ORBCoN Contacts</h2>
            <div className="space-y-8">
              {CONTACT_REGIONS.map((region) => (
                <div key={region.id} id={region.id} className="scroll-mt-24">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">{region.title}</h3>
                  <div className="grid gap-6 md:grid-cols-2">
                    {region.people.map((person) => (
                      <PersonCard key={person.name} person={person} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section id="ministry" className="scroll-mt-24">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">
              Ministry of Health Drug Programs Policy and Strategy Branch, Blood Programs Office
            </h2>
            <Card>
              <h3 className="text-lg font-semibold text-gray-900">{CONTACT_MINISTRY.name}</h3>
              {CONTACT_MINISTRY.lines.map((line) => (
                <p key={line} className="text-gray-700 mt-1">
                  {line}
                </p>
              ))}
              <a
                href={`mailto:${CONTACT_MINISTRY.email}`}
                className="block mt-3 text-primary hover:text-primary-dark break-all"
              >
                {CONTACT_MINISTRY.email}
              </a>
            </Card>
          </section>

          <section id="ttiss" className="scroll-mt-24">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">
              Transfusion Transmitted Injuries Surveillance System
            </h2>
            <div className="grid gap-6 md:grid-cols-2">
              <PersonCard person={CONTACT_TTISS} />
            </div>
          </section>

          <section id="factor" className="scroll-mt-24">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">Factor Concentrate Redistribution</h2>
            <div className="grid gap-6 md:grid-cols-2">
              {CONTACT_FACTOR.map((person) => (
                <PersonCard key={person.name} person={person} />
              ))}
            </div>
          </section>

          <section id="ontrac" className="scroll-mt-24">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">
              Ontario Nurse Transfusion Coordinators (ONTraC)
            </h2>
            <div className="grid gap-6 md:grid-cols-2">
              {CONTACT_ONTRAC.map((person) => (
                <PersonCard key={person.name} person={person} />
              ))}
            </div>
          </section>

          <section id="website" className="scroll-mt-24">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">
              Contact Information for this website
            </h2>
            <Card>
              <p className="text-gray-700 leading-relaxed">
                For inquiries please contact{' '}
                <a
                  href={`mailto:${CONTACT_WEBSITE_EMAIL}`}
                  className="text-primary hover:text-primary-dark break-all"
                >
                  {CONTACT_WEBSITE_EMAIL}
                </a>
              </p>
            </Card>
          </section>
        </div>
      </div>
    </div>
  );
}
